import React, { useCallback, useState } from 'react';
import { getCarteiraPatrimonioLiquido } from '../../../services/carteiraDiariaService';
import { getFundosGerenciados } from '../../../services/fundosGerenciadosService';
import { CarteiraDiariaPatrimonioLiquido } from '../../../types/carteiraDiaria';
import { FundosGerenciadosData } from '../../../types/fundosGerenciados';
import { ColumnDef, SortingState } from '@tanstack/react-table';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import Modal from '@mui/material/Modal';
import { useTableState } from '../../../hooks/useTableState';
import { useSnackbar } from '../../../hooks/useSnackbar';
import CadastroAtivoForm from '../../../components/CadastroAtivoForm';
import SnackbarAlert from '../../../components/SnackbarAlert';
import TableCarteiraDiaria from '../components/TableCarteiraDiaria';

const CarteiraDiaria_PL: React.FC = () => {
  const [patrimonios, setCarteiraPassivos] = useState<CarteiraDiariaPatrimonioLiquido[]>([]);
  const [fundosGerenciados, setFundosGerenciados] = useState<FundosGerenciadosData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [pageCount, setPageCount] = useState(0);
  const [isCadastroModalOpen, setIsCadastroModalOpen] = useState<boolean>(false);

  const { pagination, setPagination, sorting, setSorting, globalFilter, setGlobalFilter } = useTableState(10);
  const { snackbar, showSuccess, showError, close: closeSnackbar } = useSnackbar();

  const fetchData = useCallback(async (pageIndex: number, pageSize: number, sortBy: SortingState, globalFilter: string, dataFilter: string) => {
    setLoading(true);
    try {
      const [passivoData, fundosData] = await Promise.all([
        getCarteiraPatrimonioLiquido(pageIndex, pageSize, sortBy, globalFilter, dataFilter),
        getFundosGerenciados(),
      ]);
      setCarteiraPassivos(passivoData.results);
      setPageCount(passivoData.totalPages);
      setFundosGerenciados(fundosData);
    } catch {
      showError('Erro ao buscar dados');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  const handleCadastroSuccess = () => {
    fetchData(pagination.pageIndex, pagination.pageSize, sorting, globalFilter, '');
    showSuccess('Cadastro realizado com sucesso!');
    setIsCadastroModalOpen(false);
  };

  const getFundoGerenciadoNome = useCallback((fundoId: any): string => {
    const fundo = fundosGerenciados.find(f => f.id === fundoId);
    return fundo ? fundo.fantasia : 'Fundo desconhecido';
  }, [fundosGerenciados]);

  const columns = React.useMemo<ColumnDef<CarteiraDiariaPatrimonioLiquido>[]>(() => [
    {
      header: 'Data Posição',
      accessorKey: 'data_posicao',
      cell: (info) => {
        const rawDate = info.getValue() as string;
        const [year, month, day] = rawDate.split('-');
        return `${day}/${month}/${year}`;
      },
      enableSorting: true,
    },
    {
      header: 'Fundo Gerenciado',
      accessorFn: (row) => getFundoGerenciadoNome(row.fundo),
      id: 'fundo__fantasia',
      enableSorting: true,
    },
    { header: 'Valor por unidade', accessorKey: 'valor_por_unidade' },
    { header: 'Qtd',               accessorKey: 'quantidade' },
    { header: 'Valor Total',       accessorKey: 'patrimonio_liquido_api' },
    {
      header: 'Validado?',
      accessorFn: row => row.validated ? 'Sim' : 'Não',
      id: 'validated',
    },
  ], [getFundoGerenciadoNome]);

  return (
    <div>
      <div className="page-header">
        <h2>Carteira Diária Patrimônio Líquido</h2>
        <IconButton onClick={() => setIsCadastroModalOpen(true)}>
          <AddIcon />
        </IconButton>
      </div>
      <TableCarteiraDiaria
        columns={columns}
        data={patrimonios}
        fetchData={fetchData}
        pageCount={pageCount}
        pagination={pagination}
        setPagination={setPagination}
        sorting={sorting}
        setSorting={setSorting}
        globalFilter={globalFilter}
        setGlobalFilter={setGlobalFilter}
        isLoading={loading}
        tipoCarteira='carteira-diaria-patrimonio-liquido'
      />
      <Modal open={isCadastroModalOpen} onClose={() => setIsCadastroModalOpen(false)}>
        <div>
          <CadastroAtivoForm
            isOpen={isCadastroModalOpen}
            onRequestClose={() => setIsCadastroModalOpen(false)}
            onCadastroSuccess={handleCadastroSuccess}
            setSnackbarProps={() => {}}
          />
        </div>
      </Modal>
      <SnackbarAlert
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={closeSnackbar}
      />
    </div>
  );
};

export default CarteiraDiaria_PL;